<script setup lang="ts">
import {onBeforeUnmount, ref, watch} from "vue";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import {Loader2, Search, X} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {customerApi} from "@/container";
import {Customer} from "@/customer/interfaces/Customer";
import {phoneLabel} from "@/agenda/lib/patientSms";

export interface SelectedPatient {
    id: number;
    name: string;
    /** Phone as the search returned it; absent when the file has none. */
    phone?: string | null;
    /**
     * The consumer key, which is how the program endpoints name a patient.
     * Absent when the patient was built from a screen that does not hold it.
     */
    consumerKey?: string | null;
}

defineProps<{
    disabled?: boolean;
}>();

const patient = defineModel<SelectedPatient | null>({required: true});

const query = ref('');
const results = ref<Customer[]>([]);
const searching = ref(false);
const failed = ref(false);

const displayName = (customer: Customer) => {
    return customer.information?.name
        ?? `${customer.information?.first_name ?? ''} ${customer.information?.last_name ?? ''}`.trim();
};

/**
 * The second line of a result, what tells namesakes apart before a booking,
 * a text or a journey goes to the wrong one: birth date and age, then the
 * mobile. A patient booked online often has no birth date; that is said
 * rather than left blank.
 */
const identityHint = (customer: Customer): string => {
    const birthDate = customer.information?.birth_date;
    const birth = birthDate && dayjs(birthDate).isValid()
        ? `${dayjs(birthDate).format('DD/MM/YYYY')} · ${dayjs().diff(birthDate, 'year')} ans`
        : 'Date de naissance non renseignée';
    const phone = customer.information?.phone;

    return `${birth} · ${phone ? phoneLabel(phone) : 'sans mobile'}`;
};

/**
 * Keywords of the most recent search: responses of superseded searches are
 * dropped, otherwise a slow answer for "mar" could overwrite the results
 * already shown for "marchand" and have the wrong patient picked.
 */
let latestKeywords = '';

const search = debounce(async (keywords: string) => {
    latestKeywords = keywords;

    if (keywords.trim().length < 2) {
        results.value = [];
        failed.value = false;
        searching.value = false;

        return;
    }

    searching.value = true;
    failed.value = false;

    try {
        const found = await customerApi().search(keywords);

        if (latestKeywords !== keywords) {
            return;
        }

        results.value = found;
    } catch {
        // A failure must be said, not spun on: without this the panel would
        // stay on its spinner for good and no patient could be picked.
        if (latestKeywords !== keywords) {
            return;
        }

        results.value = [];
        failed.value = true;
    } finally {
        // Left alone when a newer search superseded this one: that one owns
        // the flag now.
        if (latestKeywords === keywords) {
            searching.value = false;
        }
    }
}, 350);

/**
 * Watched rather than bound to @input: the listener would run before the
 * v-model wrote the new value, searching one keystroke behind.
 */
watch(query, (keywords) => {
    searching.value = true;
    search(keywords);
});

const selectPatient = (customer: Customer) => {
    patient.value = {
        id: customer.id,
        name: displayName(customer),
        phone: customer.information?.phone ?? null,
        consumerKey: customer.consumer_key ?? null
    };
    query.value = '';
    results.value = [];
    failed.value = false;
};

/** Retry the last search after a failure. */
const retry = () => {
    search(query.value);
    search.flush();
};

const clearPatient = () => {
    patient.value = null;
};

onBeforeUnmount(() => {
    search.cancel();
});
</script>

<template>
  <div>
    <div
      v-if="patient"
      class="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
    >
      <span class="truncate font-medium">{{ patient.name }}</span>
      <Button
        v-if="!disabled"
        type="button"
        variant="ghost"
        size="icon"
        class="size-6"
        aria-label="Retirer le patient"
        @click="clearPatient"
      >
        <X class="size-4" />
      </Button>
    </div>
    <div v-else class="relative">
      <Search class="absolute left-2.5 top-2.5 size-4 text-gray-400" />
      <Input
        v-model="query"
        class="pl-8"
        placeholder="Rechercher un patient…"
        :disabled="disabled"
      />
      <div
        v-if="query.trim().length >= 2"
        class="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
      >
        <div v-if="searching" class="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
          <Loader2 class="size-4 animate-spin" />
          Recherche…
        </div>
        <div v-else-if="failed" class="flex items-center justify-between gap-2 px-3 py-2 text-sm">
          <span class="text-gray-500">La recherche a échoué.</span>
          <Button type="button" variant="ghost" size="sm" @click="retry">
            Réessayer
          </Button>
        </div>
        <template v-else>
          <button
            v-for="customer in results"
            :key="customer.id"
            type="button"
            class="block w-full px-3 py-1.5 text-left hover:bg-slate-100"
            @click="selectPatient(customer)"
          >
            <span class="block truncate text-sm">{{ displayName(customer) }}</span>
            <span class="block truncate text-xs tabular-nums text-gray-500">{{ identityHint(customer) }}</span>
          </button>
          <p v-if="!results.length" class="px-3 py-2 text-sm text-gray-500">
            Aucun patient trouvé.
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
